import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog/Dialog';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { SchedulingPausedStep } from '@/features/scheduling/components/SchedulingPaused/SchedulingPausedStep';
import { useIsSchedulingBlocked } from '@/features/systemBanner/hooks/useIsSchedulingBlocked';
import { useErrorTranslation } from '@/hooks/useErrorTranslation';
import { DialogDescription } from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { useCancelFlow } from './context/useCancelFlow';
import { CancelStep } from './steps/CancelStep/CancelStep';
import { ErrorStep } from './steps/ErrorStep/ErrorStep';
import { LateCancelErrorStep } from './steps/LateCancelErrorStep/LateCancelErrorStep';
import { SuccessStep } from './steps/SuccessStep/SuccessStep';

const CancelDialogContent = () => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'cancelDialog' });
  const { translateError } = useErrorTranslation();
  const { currentStep, closeCancelDialog, data } = useCancelFlow();
  const isSchedulingBlocked = useIsSchedulingBlocked();

  const getContent = () => {
    if (isSchedulingBlocked && currentStep !== 'success') {
      return <SchedulingPausedStep onClose={closeCancelDialog} />;
    }

    if (data.config.isLoading) {
      return (
        <div className='flex flex-1 items-center justify-center p-6' role='status' aria-live='polite'>
          <Spinner className='size-20 stroke-1' />
        </div>
      );
    }

    if (data.config.error) {
      return (
        <div className='flex flex-1 items-center justify-center p-6' role='alert' aria-live='assertive'>
          <p className='text-text-default-secondary'>{translateError(data.config.error)}</p>
        </div>
      );
    }

    switch (currentStep) {
      case 'cancel': {
        return <CancelStep />;
      }
      case 'success': {
        return <SuccessStep />;
      }
      case 'lateCancelError': {
        return <LateCancelErrorStep />;
      }
      case 'error': {
        return <ErrorStep />;
      }
    }
  };

  return (
    <DialogContent className='desktop:!max-h-[840px]'>
      <DialogHeader>
        <DialogTitle>{t('title')}</DialogTitle>
      </DialogHeader>
      <DialogDescription className='sr-only'>{t('description')}</DialogDescription>
      {getContent()}
    </DialogContent>
  );
};

export const CancelDialog = () => {
  const { isCancelDialogOpen, closeCancelDialog } = useCancelFlow();

  return (
    <Dialog open={isCancelDialogOpen} onOpenChange={closeCancelDialog}>
      <CancelDialogContent />
    </Dialog>
  );
};
